import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import PetSearch from '../PetSearch';
import ServiceSelector from '../ServiceSelector';
import { NewBookingAlert } from '@spike/new-booking-model';
import { useApiClientWrapper, useAuth, useMarketplace, usePermissions } from 'hooks';
import Pet from '@spike/pet-model';
import Staff from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { fetchAlertsThunk } from '@spike/new-bookings-action';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import DateSelector from '../DateSelector';
import {
    BoardingData,
    DaycareData,
    Daycare as DaycareModel,
    Boarding as BoardingModel,
    Discount as DiscountModel,
    Feeding,
    Medication,
    PetServiceWrapper,
    versionPetAndServices,
    Transportation as TransportationModel,
    Transportation as TransportationData,
    Walking as WalkingData,
    Training as TrainingData,
    DataVaraible
} from 'model/NewMultipleBooking';
import StaffSelector from '../StaffSelector/StaffSelector';
import Discount from '../Discount';
import ServiceTypeTags from '../Daycare/UI/ServiceTypeTags';
import FeedingsAndMedications from '../Daycare/FeedingsAndMedications/FeedingsAndMedications';
import clsx from 'clsx';
import Marketplace from '@spike/marketplace-model';
import { BusinessHours } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import Daycare from '../Daycare/Daycare';
import Boarding from '../Boarding/Boarding';
import TransportationComponent from '../Transportation/TransportationComponent';
import WalkingComponent from '../Walking/WalkingComponent';
import TrainingComponent from '../Training/TrainingComponent';
import ActivityHistoryDrawer from 'components/OrderDetail/Tabs/Activities/ActivityHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentListItem } from '@spike/appointments-list-model';
import MembershipsComponent from '../Memberships/MembershipsComponent';
import type { MembershipsSelection } from '../Memberships/UI/MembershipsSelector/MembershipsSelector';
import type { Enrollment } from '@spike/membership-model';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import type { EnrollmentAnchor } from '../utils/enrollmentAnchor';
import lockStyles from '../UI/priceFieldInteractionLock.module.scss';

interface Props {
    parentId: number;
    petId?: number;
    appointmentId?: number;
    service?: PetServiceWrapper;
    staffId?: number;
    staffIdPreselected?: number;
    slotTimes?: Array<string>;
    versionPetAndServices?: versionPetAndServices;
    date?: Moment;
    dateEnd?: Moment;
    petsUsed?: Array<number>;
    discount: DiscountModel;
    servicesUsed?: Array<number>;
    petWithoutSelect?: boolean;
    defaultDiscount?: string;
    daycare?: DaycareData;
    boarding?: BoardingData;
    transportation?: TransportationData;
    training?: TrainingData;
    walking?: WalkingData;
    recurrentInfo?: boolean;
    nights?: number;
    flow:
        | 'grooming'
        | 'dental'
        | 'daycare'
        | 'boarding'
        | 'transportation'
        | 'walking'
        | 'training'
        | 'memberships'
        | 'evaluation'
        | undefined;
    isEvaluation?: boolean;
    showMembershipsTag?: boolean;
    membershipsTagLoading?: boolean;
    disableMembershipsAutoSelect?: boolean;
    hideServiceTypeTags?: boolean;
    onChangeDate: (value: Moment) => void;
    onChangePet: (value: Pet) => void;
    onChangeService: (value: PetServiceWrapper, data: boolean) => void;
    onChangeStaff: (value?: Staff) => void;
    onChangeDiscount: (discount: DiscountModel) => void;
    onChangeFlow: (
        flow:
            | 'grooming'
            | 'dental'
            | 'daycare'
            | 'boarding'
            | 'transportation'
            | 'walking'
            | 'training'
            | 'memberships'
            | 'evaluation'
            | undefined
    ) => void;
    onShowFeedingAndMedications: (show: boolean) => void;
    onSaveDaycare: (daycare: DaycareData) => void;
    onSaveBoarding: (boarding: BoardingData) => void;
    onDaycareNext?: (daycare: DaycareModel) => void;
    onDaycareChange?: (daycare: DaycareModel, selectedDates: Array<Moment>) => void;
    onBoardingNext?: (boarding: BoardingModel) => void;
    onBoardingServiceChange?: (service: PetServiceWrapper) => void;
    onBoardingPeriodChange?: (from: Moment, to: Moment) => void;
    onClearMembership?: () => void;
    onBoardingRoomChanged?: () => void;
    boardingMembershipAlertServiceId?: number;
    onSaveTransportation: (transportation: TransportationData) => void;
    onSaveTraining: (training: TrainingData) => void;
    onSaveWalking: (walking: WalkingData) => void;
    draftSessionBookings?: Array<DataVaraible>;
    onViewAllRecentBookings?: () => void;
    onMembershipServiceSelect?: (selection: MembershipsSelection) => void;
    membershipPetServicesLoading?: boolean;
    enrollment?: Enrollment;
    enrollmentAnchor?: EnrollmentAnchor;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {},
        sectionContainer: {
            paddingTop: 18,
            [theme.breakpoints.up('md')]: {
                paddingTop: 20
            }
        },
        noShow: {
            display: 'none'
        }
    })
);

const getTodayClosingTime = (from: Moment, marketplace: Marketplace): Moment => {
    const dayOfWeek = from.format('dddd').toLowerCase();
    const businessHours: BusinessHours | null =
        marketplace.schedule.week[dayOfWeek as keyof typeof marketplace.schedule.week];
    const [hour, minute] = businessHours?.close.split(':').map(Number) || [17, 0];
    return from.clone().hour(hour).minute(minute);
};

const getTodayOpeningTime = (from: Moment, marketplace: Marketplace): Moment => {
    const dayOfWeek = from.format('dddd').toLowerCase();
    const businessHours: BusinessHours | null =
        marketplace.schedule.week[dayOfWeek as keyof typeof marketplace.schedule.week];
    const [hour, minute] = businessHours?.open?.split(':').map(Number) || [8, 0];
    return from
        .clone()
        .tz(marketplace.schedule.timeZone)
        .startOf('day')
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);
};

const createEmptyDaycare = (from: Moment, marketplace: Marketplace): DaycareData => {
    return {
        daycare: {
            from: from,
            to: moment(getTodayClosingTime(from, marketplace)).tz(marketplace.schedule.timeZone),
            service: undefined,
            room: undefined,
            roomUnitId: undefined,
            price: 0,
            priceForHour: 0,
            discount: {
                type: 'amount',
                value: 0
            },
            selectedDates: [from.clone().tz(marketplace.schedule.timeZone).startOf('day')]
        },
        feedings: [],
        medications: []
    };
};

const createEmptyBoarding = (from: Moment, marketplace: Marketplace, dateEnd?: Moment): BoardingData => {
    const to = dateEnd?.isValid()
        ? dateEnd.clone().tz(marketplace.schedule.timeZone)
        : from.clone().add(1, 'day').tz(marketplace.schedule.timeZone);
    return {
        boarding: {
            from: from,
            to: to,
            service: undefined,
            room: undefined,
            roomUnitId: undefined,
            price: 0
        },
        feedings: [],
        medications: []
    };
};

const createEmptyTransportation = (from: Moment, marketplace: Marketplace): TransportationModel => {
    const tz = marketplace.schedule.timeZone;
    const openingTime = getTodayOpeningTime(from, marketplace);
    const pickupTime = {
        from: openingTime.clone(),
        to: openingTime.clone().add(1, 'hour')
    };
    const dropOffTime = {
        from: openingTime.clone().add(3, 'hours'),
        to: openingTime.clone().add(4, 'hours')
    };
    return {
        transportType: 'pickup',
        service: undefined,
        selectedStaff: undefined,
        vehicle: undefined,
        selectedDates: [from.clone().tz(tz).startOf('day')],
        pickup: {
            price: 0,
            discount: undefined,
            time: pickupTime,
            address: '',
            instructions: ''
        },
        dropOff: {
            price: 0,
            discount: undefined,
            time: dropOffTime,
            address: '',
            instructions: ''
        }
    };
};

const createEmptyTraining = (from: Moment, marketplace: Marketplace): TrainingData => {
    const tz = marketplace.schedule.timeZone;
    const to = from.clone().add(1, 'hour').tz(tz);
    return {
        from: from,
        to: to,
        service: undefined,
        price: 0,
        discount: {
            type: 'amount',
            value: 0
        },
        staffId: undefined,
        selectedDates: [from.clone().tz(tz).startOf('day')]
    };
};

const createEmptyWalking = (from: Moment, marketplace: Marketplace): WalkingData => {
    const tz = marketplace.schedule.timeZone;
    const to = from.clone().add(1, 'hour').tz(tz);
    return {
        from: from,
        to: to,
        service: undefined,
        price: 0,
        discount: {
            type: 'amount',
            value: 0
        },
        staffId: undefined,
        selectedDates: [from.clone().tz(tz).startOf('day')]
    };
};

export const AddOrEditService: FunctionComponent<Props> = props => {
    const marketplace = useMarketplace();
    const classes = useStyles();
    const auth = useAuth();
    const apiClientWrapper = useApiClientWrapper();
    const permissions = usePermissions();
    const canApplyDiscount = permissions.checkout.applyDiscounts;
    const hasDiscount = props.discount?.type !== 'none' && (props.discount?.value ?? 0) > 0;
    const dispatch = useDispatch();
    const [showService, setShowService] = useState(() => Boolean(props.petId));
    const [showActivityHistory, setShowActivityHistory] = useState(false);
    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);
    const [date, setDate] = useState<Moment | undefined>(props.date);
    const [selectedPetForHistory, setSelectedPetForHistory] = useState<Pet | undefined>(undefined);
    const [showFeedingAndMedications, setShowFeedingAndMedications] = useState(false);
    const [daycare, setDaycare] = useState<DaycareData>(() => {
        if (props.daycare) {
            return props.daycare;
        }
        return createEmptyDaycare(props.date!, marketplace);
    });
    const [boarding, setBoarding] = useState<BoardingData>(() => {
        if (props.boarding) {
            return props.boarding;
        }
        return createEmptyBoarding(props.date!, marketplace);
    });
    const didUserChangeBoardingPeriodRef = useRef(false);
    const [transportation, setTransportation] = useState<TransportationModel>(() => {
        if (props.transportation) {
            return props.transportation;
        }
        return createEmptyTransportation(props.date!, marketplace);
    });
    const [walking, setWalking] = useState<WalkingData>(() => {
        if (props.walking) {
            return props.walking;
        }
        return createEmptyWalking(props.date!, marketplace);
    });

    const [training, setTraining] = useState<TrainingData>(() => {
        if (props.training) {
            return props.training;
        }
        return createEmptyTraining(props.date!, marketplace);
    });

    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);
    const pets = useSelector<RootState, Array<Pet>>(state => state.pets.list);
    const filteredPets = useMemo(() => pets.filter(pet => !pet.deleted && !pet.deceased && pet.active), [pets]);

    const canShowStaff = useMemo(() => Boolean(props.service?.service?.id), [props.service?.service?.id]);

    // Walking and Training run their own check_alerts with the service and staff picked inside
    // their form. Both write to the same alerts slice, so firing here would race it and win at random.
    useEffect(() => {
        if (!date || props.flow === 'walking' || props.flow === 'training') return;
        dispatch(
            fetchAlertsThunk(
                apiClientWrapper,
                props.parentId || 0,
                props.petId || 0,
                date.clone().tz('utc'),
                false,
                props.service?.service?.id,
                props.staffId,
                undefined
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, props.service?.service?.id, props.staffId, props.parentId, props.petId, props.flow]);

    useEffect(() => {
        if (date) props.onChangeDate(date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Staff is per location: reload when the booking location changes.
    useEffect(() => {
        dispatch(fetchStaffThunk(apiClientWrapper.marketplaceId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiClientWrapper.marketplaceId]);

    useEffect(() => {
        if (!props.daycare || showFeedingAndMedications || props.flow !== 'daycare') {
            return;
        }
        setDaycare(prev => {
            if (!prev?.daycare?.service?.serviceId) {
                return props.daycare!;
            }
            const incoming = props.daycare!;
            const local = prev.daycare;
            const incomingDaycare = incoming.daycare;
            if (!local?.from?.isValid() || !incomingDaycare?.from?.isValid()) {
                return incoming;
            }
            const selectedDatesDiffer = (() => {
                if (props.versionPetAndServices === versionPetAndServices.edit) {
                    return false;
                }
                const localDates = local.selectedDates ?? [];
                const incomingDates = incomingDaycare.selectedDates ?? [];
                if (localDates.length !== incomingDates.length) {
                    return true;
                }
                const localKeys = localDates.map(d => d.clone().startOf('day').format('YYYY-MM-DD')).sort();
                const incomingKeys = incomingDates.map(d => d.clone().startOf('day').format('YYYY-MM-DD')).sort();
                return localKeys.some((key, index) => key !== incomingKeys[index]);
            })();
            const scheduleOrPricingDiffers =
                !local.from.isSame(incomingDaycare.from, 'minute') ||
                !local.to?.isSame(incomingDaycare.to, 'minute') ||
                local.price !== incomingDaycare.price ||
                local.discount?.type !== incomingDaycare.discount?.type ||
                local.discount?.value !== incomingDaycare.discount?.value ||
                local.roomUnitId !== incomingDaycare.roomUnitId ||
                local.room?.id !== incomingDaycare.room?.id ||
                selectedDatesDiffer;
            if (scheduleOrPricingDiffers) {
                return {
                    ...incoming,
                    daycare: {
                        ...incomingDaycare,
                        from: local.from,
                        to: local.to,
                        price: local.price,
                        priceForHour: local.priceForHour,
                        discount: local.discount,
                        service: local.service ? { ...local.service, price: local.price } : incomingDaycare.service,
                        selectedDates:
                            props.versionPetAndServices === versionPetAndServices.edit
                                ? [local.from.clone().tz(marketplace.schedule.timeZone).startOf('day')]
                                : local.selectedDates?.length
                                ? local.selectedDates
                                : incomingDaycare.selectedDates,
                        room: local.room ?? incomingDaycare.room,
                        roomUnitId: local.roomUnitId ?? incomingDaycare.roomUnitId,
                        enrollment: local.enrollment ?? incomingDaycare.enrollment,
                        membershipItem: local.membershipItem ?? incomingDaycare.membershipItem
                    }
                };
            }
            return incoming;
        });
    }, [props.daycare, showFeedingAndMedications, props.flow]);

    const handleDaycareChange = useCallback(
        (daycareModel: DaycareModel, selectedDates: Array<Moment>) => {
            const resolvedDates =
                props.versionPetAndServices === versionPetAndServices.edit
                    ? [daycareModel.from.clone().tz(marketplace.schedule.timeZone).startOf('day')]
                    : selectedDates;
            setDaycare(prev => ({
                feedings: prev?.feedings ?? [],
                medications: prev?.medications ?? [],
                daycare: { ...daycareModel, selectedDates: resolvedDates }
            }));
            props.onDaycareChange?.(daycareModel, resolvedDates);
        },
        [props.onDaycareChange, props.versionPetAndServices, marketplace.schedule.timeZone]
    );

    const handleBoardingPeriodChange = useCallback(
        (from: Moment, to: Moment) => {
            didUserChangeBoardingPeriodRef.current = true;
            setBoarding(prev => {
                if (prev?.boarding == null) {
                    return prev;
                }
                return {
                    ...prev,
                    boarding: {
                        ...prev.boarding,
                        from: from.clone(),
                        to: to.clone()
                    }
                };
            });
            props.onBoardingPeriodChange?.(from, to);
        },
        [props.onBoardingPeriodChange]
    );

    useEffect(() => {
        if (!props.boarding) {
            return;
        }
        setBoarding(prev => {
            if (
                prev?.boarding?.service?.selectedOffer != null &&
                props.boarding?.boarding?.service?.selectedOffer == null
            ) {
                return prev;
            }
            const incoming = props.boarding!;
            if (prev?.boarding?.from?.isValid() !== true || incoming.boarding?.from?.isValid() !== true) {
                return incoming;
            }
            const localFrom = prev.boarding.from;
            const localTo = prev.boarding.to;
            const incomingFrom = incoming.boarding.from;
            const incomingTo = incoming.boarding.to;
            const periodDiffersFromParent =
                !localFrom.isSame(incomingFrom, 'day') || !localTo.isSame(incomingTo, 'day');
            const isEditMode = props.versionPetAndServices === versionPetAndServices.edit;
            if (periodDiffersFromParent && !isEditMode && didUserChangeBoardingPeriodRef.current) {
                return {
                    ...incoming,
                    boarding: {
                        ...incoming.boarding,
                        from: localFrom,
                        to: localTo
                    }
                };
            }
            return incoming;
        });
    }, [props.boarding]);

    useEffect(() => {
        if (props.transportation) {
            setTransportation(props.transportation);
        }
    }, [props.transportation]);

    useEffect(() => {
        if (props.walking) {
            setWalking(props.walking);
        }
    }, [props.walking]);

    useEffect(() => {
        if (props.training) {
            setTraining(props.training);
        }
    }, [props.training]);

    useEffect(() => {
        if (!props.petId) {
            setSelectedPetForHistory(undefined);
            return;
        }

        setShowService(true);
        const selectedPet = filteredPets.find(pet => pet.id === props.petId);
        setSelectedPetForHistory(selectedPet);
    }, [filteredPets, props.petId]);

    useNonInitialEffect(() => {
        if (props.flow === 'daycare' && !props.daycare) {
            setDaycare(createEmptyDaycare(props.date!, marketplace));
        }
    }, [props.petId]);

    useNonInitialEffect(() => {
        if (props.flow === 'boarding' && !props.boarding) {
            setBoarding(createEmptyBoarding(props.date!, marketplace, props.dateEnd));
        }
    }, [props.petId]);

    useNonInitialEffect(() => {
        if (props.flow === 'transportation' && !props.transportation) {
            setTransportation(createEmptyTransportation(props.date!, marketplace));
        }
    }, [props.petId]);

    useNonInitialEffect(() => {
        if (props.flow === 'walking' && !props.walking) {
            setWalking(createEmptyWalking(props.date!, marketplace));
        }
        if (props.flow === 'training' && !props.training) {
            setTraining(createEmptyTraining(props.date!, marketplace));
        }
    }, [props.petId]);

    const handlerPet = (value: Pet) => {
        setShowService(true);
        setSelectedPetForHistory(value);
        props.onChangePet(value);
    };

    const handlerOpenActivityHistory = (pet: Pet) => {
        setSelectedPetForHistory(pet);
        setShowActivityHistory(true);
    };

    const handlerChangeDate = (value: Moment) => {
        setDate(value);
        props.onChangeDate(value);
    };

    const handlerService = (value: PetServiceWrapper, data: boolean) => {
        props.onChangeService(value, data);
    };

    const handlerStaff = (staffSelected?: Staff) => {
        props.onChangeStaff(staffSelected);
    };

    const handlerBackFeedingsAndMedications = () => {
        setShowFeedingAndMedications(false);
        props.onShowFeedingAndMedications(false);
    };

    const handlerNextDaycareFeedingsAndMedications = (daycareModel: DaycareModel) => {
        setDaycare(prev => ({ ...prev, daycare: daycareModel }));
        props.onDaycareNext?.(daycareModel);
        setShowFeedingAndMedications(true);
        props.onShowFeedingAndMedications(true);
    };

    const handlerNextBoardingFeedingsAndMedications = (boardingModel: BoardingModel) => {
        setBoarding(prev => ({ ...prev, boarding: boardingModel }));
        props.onBoardingNext?.(boardingModel);
        setShowFeedingAndMedications(true);
        props.onShowFeedingAndMedications(true);
    };

    const handlerSave = (feedings: Array<Feeding>, medications: Array<Medication>) => {
        const daycareChanged = { ...daycare, feedings: feedings, medications: medications } as DaycareData;
        const boardingChanged = { ...boarding, feedings: feedings, medications: medications } as BoardingData;
        if (props.flow === 'daycare') {
            setDaycare(daycareChanged);
            props.onSaveDaycare(daycareChanged);
        } else if (props.flow === 'boarding') {
            setBoarding(boardingChanged);
            props.onSaveBoarding(boardingChanged);
        }
    };

    const petsView = (
        <Box className={classes.sectionContainer}>
            <PetSearch
                parentID={props.parentId}
                petId={props.petId}
                multiplePets={true}
                petsUsed={props.petsUsed}
                readOnly={props.versionPetAndServices !== versionPetAndServices.another}
                hideSelect={props.petWithoutSelect}
                handlerPet={handlerPet}
                onOpenActivityHistory={handlerOpenActivityHistory}
            />
        </Box>
    );

    const dateView = (
        <Box className={classes.sectionContainer}>
            <DateSelector
                date={date!}
                slotTimes={props.slotTimes}
                withoutBorder
                showAlertBusinessHour={
                    alerts && alerts?.filter(alert => alert.alertType === 'date_time')?.length > 0 ? true : false
                }
                multiplePet={true}
                onChangeDate={handlerChangeDate}
                alerts={alerts?.filter(alert => alert.alertType === 'unified')}
            />
        </Box>
    );

    const serviceView = (
        <Box className={classes.sectionContainer}>
            <ServiceSelector
                customizable
                selectedService={props.service}
                servicesSelected={props.servicesUsed || []}
                clientId={props.parentId!}
                petId={props.petId!}
                multiplePets={true}
                isEvaluation={props.isEvaluation}
                onSelectService={handlerService}
            />
        </Box>
    );

    const discountView = (
        <Box
            className={clsx(classes.sectionContainer, {
                [lockStyles.interactionLock]: !canApplyDiscount && hasDiscount,
                [lockStyles.hiddenComponent]: !canApplyDiscount && !hasDiscount
            })}
        >
            <Discount
                discount={props.discount}
                max={
                    props.discount?.type === 'percentage'
                        ? 100
                        : props.discount?.type === 'amount'
                        ? props.service?.price
                        : 0.5
                }
                default={props.defaultDiscount}
                onChangeDiscount={props.onChangeDiscount}
            />
        </Box>
    );

    const staffView = (
        <Box className={classes.sectionContainer}>
            <StaffSelector
                key={props.service?.service?.id || 'no-service'}
                selectedStaff={props.staffId || props.staffIdPreselected || auth.user!.staffId}
                service={props.service}
                onSelectStaff={handlerStaff}
                alerts={alerts?.filter(alert => alert.alertType === 'staff')}
            />
        </Box>
    );

    const selectServiceTypeView = (
        <Box className={classes.sectionContainer}>
            <ServiceTypeTags
                selectedServiceType={props.flow}
                onSelectServiceType={props.onChangeFlow}
                showMembershipsTag={props.showMembershipsTag}
                membershipsTagLoading={props.membershipsTagLoading}
                disableMembershipsAutoSelect={
                    props.disableMembershipsAutoSelect ||
                    props.versionPetAndServices === versionPetAndServices.edit ||
                    props.isEvaluation
                }
            />
        </Box>
    );

    const feedingAndMedicationsView = (
        <Box className={classes.sectionContainer}>
            <FeedingsAndMedications
                parentId={props.parentId!}
                petId={props.petId!}
                flow={props.flow as 'daycare' | 'boarding'}
                feedings={props.flow === 'boarding' ? boarding.feedings || [] : daycare.feedings || []}
                medications={props.flow === 'boarding' ? boarding.medications || [] : daycare.medications || []}
                versionPetAndServices={props.versionPetAndServices}
                from={props.flow === 'boarding' ? boarding.boarding?.from! : daycare.daycare?.from!}
                to={props.flow === 'boarding' ? boarding.boarding?.to! : daycare.daycare?.to!}
                onBack={handlerBackFeedingsAndMedications}
                onSave={handlerSave}
            />
        </Box>
    );

    return (
        <>
            <Box className={classes.container}>
                {!showFeedingAndMedications && petsView}
                {!showFeedingAndMedications && !props.hideServiceTypeTags && (
                    <div
                        className={clsx({
                            [classes.noShow]:
                                props.versionPetAndServices === versionPetAndServices.edit || props.isEvaluation
                        })}
                    >
                        {selectServiceTypeView}
                    </div>
                )}
                {!showFeedingAndMedications && props.membershipsTagLoading && !props.hideServiceTypeTags ? (
                    <Box
                        className={classes.sectionContainer}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{ minHeight: 220 }}
                    >
                        <Spinner size={48} />
                    </Box>
                ) : (
                    <>
                        {props.flow === 'grooming' || props.flow === 'dental' || props.flow === 'evaluation' ? (
                            <>
                                {showService && dateView}
                                {showService &&
                                    props.versionPetAndServices !== versionPetAndServices.edit &&
                                    !props.isEvaluation &&
                                    props.flow === 'grooming' && (
                                        <Box className={classes.sectionContainer}>
                                            <RecentlyBooked
                                                clientId={props.parentId}
                                                petId={props.petId}
                                                refreshTrigger={recentlyBookedRefreshKey}
                                                onViewAll={() => {
                                                    setShowAppointmentHistoryDrawer(true);
                                                    props.onViewAllRecentBookings?.();
                                                }}
                                                onAppointmentClick={(appointment: AppointmentListItem) => {
                                                    if (appointment.orderId != null && appointment.bookingId != null) {
                                                        setShowOrderDetail({
                                                            orderId: appointment.orderId,
                                                            bookingId: appointment.bookingId
                                                        });
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                {showService && serviceView}
                                {canShowStaff && discountView}
                                {canShowStaff && staffView}
                            </>
                        ) : null}
                        {props.flow === 'daycare' && !showFeedingAndMedications && (
                            <>
                                <Daycare
                                    clientId={props.parentId}
                                    petId={props.petId!}
                                    daycare={daycare.daycare!}
                                    edit={props.versionPetAndServices === versionPetAndServices.edit}
                                    servicesUsed={props.servicesUsed || []}
                                    enrollment={props.enrollment ?? props.service?.enrollment}
                                    membershipItem={props.service?.membershipItem}
                                    enrollmentAnchor={props.enrollmentAnchor}
                                    onNext={handlerNextDaycareFeedingsAndMedications}
                                    onChange={handleDaycareChange}
                                />
                            </>
                        )}

                        {props.flow === 'boarding' && !showFeedingAndMedications && (
                            <>
                                <Boarding
                                    clientId={props.parentId}
                                    petId={props.petId!}
                                    boarding={boarding.boarding!}
                                    nights={props.nights}
                                    servicesUsed={props.servicesUsed || []}
                                    edit={props.versionPetAndServices === versionPetAndServices.edit}
                                    enrollment={props.enrollment}
                                    enrollmentAnchor={props.enrollmentAnchor}
                                    membershipItem={props.service?.membershipItem}
                                    boardingMembershipAlertServiceId={props.boardingMembershipAlertServiceId}
                                    onNext={handlerNextBoardingFeedingsAndMedications}
                                    onBoardingServiceChange={props.onBoardingServiceChange}
                                    onBoardingPeriodChange={handleBoardingPeriodChange}
                                    onClearMembership={props.onClearMembership}
                                    onRoomChanged={props.onBoardingRoomChanged}
                                />
                            </>
                        )}

                        {props.flow === 'transportation' && !showFeedingAndMedications && (
                            <>
                                <TransportationComponent
                                    clientId={props.parentId}
                                    petId={props.petId!}
                                    edit={props.versionPetAndServices === versionPetAndServices.edit}
                                    transportation={transportation}
                                    enrollment={props.enrollment}
                                    membershipItem={props.service?.membershipItem}
                                    enrollmentAnchor={props.enrollmentAnchor}
                                    onNext={props.onSaveTransportation}
                                />
                            </>
                        )}
                        {props.flow === 'training' && !showFeedingAndMedications && (
                            <>
                                <TrainingComponent
                                    petId={props.petId!}
                                    clientId={props.parentId!}
                                    training={training}
                                    appointmentId={props.appointmentId}
                                    servicesUsed={props.servicesUsed}
                                    inHomeService={training.service?.inHomeService}
                                    enrollment={props.enrollment}
                                    membershipItem={props.service?.membershipItem}
                                    enrollmentAnchor={props.enrollmentAnchor}
                                    versionPetAndServices={props.versionPetAndServices}
                                    draftSessionBookings={props.draftSessionBookings}
                                    onNext={props.onSaveTraining}
                                />
                            </>
                        )}
                        {props.flow === 'walking' && !showFeedingAndMedications && (
                            <>
                                <WalkingComponent
                                    clientId={props.parentId!}
                                    petId={props.petId!}
                                    walking={walking}
                                    appointmentId={props.appointmentId}
                                    inHomeService={walking.service?.inHomeService}
                                    servicesUsed={props.servicesUsed || []}
                                    enrollment={props.enrollment}
                                    membershipItem={props.service?.membershipItem}
                                    enrollmentAnchor={props.enrollmentAnchor}
                                    versionPetAndServices={props.versionPetAndServices}
                                    draftSessionBookings={props.draftSessionBookings}
                                    onNext={props.onSaveWalking}
                                />
                            </>
                        )}
                        {props.flow === 'memberships' && !showFeedingAndMedications && (
                            <>
                                <MembershipsComponent
                                    clientId={props.parentId}
                                    petId={props.petId!}
                                    date={date!}
                                    onChangeDate={handlerChangeDate}
                                    versionPetAndServices={props.versionPetAndServices}
                                    isEvaluation={props.isEvaluation}
                                    onViewAllRecentBookings={props.onViewAllRecentBookings}
                                    onMembershipServiceSelect={props.onMembershipServiceSelect}
                                    membershipPetServicesLoading={props.membershipPetServicesLoading}
                                />
                            </>
                        )}
                    </>
                )}

                {showFeedingAndMedications && feedingAndMedicationsView}
            </Box>

            <ActivityHistoryDrawer
                open={showActivityHistory}
                onBack={() => setShowActivityHistory(false)}
                pets={filteredPets}
                selectedPet={selectedPetForHistory}
                customerId={props.parentId}
            />
            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={props.parentId}
                petId={props.petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({
                            orderId: appointment.orderId,
                            bookingId: appointment.bookingId
                        });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </>
    );
};

export default AddOrEditService;
